/**
 * @file: useCollapsible.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCallback, useRef, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

export const useCollapsible = () => {
  const progress = useSharedValue(1);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const isCollapsing = useRef(false);

  const onPress = useCallback(() => {
    if (progress.value > 0 && !isCollapsing.current) {
      // collapse
      isCollapsing.current = true;
      progress.value = withTiming(0, {
        duration: 150,
        easing: Easing.inOut(Easing.linear),
      });
    } else {
      isCollapsing.current = false;
      progress.value = withTiming(1, {
        duration: 150,
        easing: Easing.inOut(Easing.linear),
      });
    }
  }, [progress]);

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      if (contentHeight !== e.nativeEvent.layout.height) {
        setContentHeight(e.nativeEvent.layout.height);
      }
    },
    [contentHeight],
  );

  const animatedStyle = useAnimatedStyle(() => {
    if (!contentHeight) return {};
    const height = interpolate(progress.value, [0, 1], [0, contentHeight]);
    return { height };
  });

  const caretStyle = useAnimatedStyle(() => {
    const angle = interpolate(progress.value, [0, 1], [180, 0]);
    return {
      transform: [
        {
          rotate: `${angle}deg`,
        },
      ],
    };
  });

  return { onLayout, progress, onPress, animatedStyle, caretStyle };
};
